import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BookOpen, MessageCircle, PlugZap, RefreshCw } from "lucide-react";

import { Button } from "@/shared/ui/button";

/**
 * 客服舱首次进入的空状态引导(1004 T7)。
 *
 * ★原来零会话时只有一排 0 + 表格里「暂无会话记录」+ 表格下又一句「暂无会话」—— 用户不知道下一步。
 *   这里给三步:接渠道(→ /channels?tab=web,网页 / H5 接入点不依赖企微;企微页签同页可切)、自己试聊一次(发完回来刷新)、补知识库(→ /knowledge)。
 * ★只在「真的一条会话都没有」时出(没有搜索 / 状态筛选);筛出 0 条仍是表格自己的空态。
 */
export function KefuEmptyGuide({ onRefresh }: { onRefresh: () => void }) {
  const { t } = useTranslation("customer-service");
  const navigate = useNavigate();
  const steps = [
    {
      icon: PlugZap,
      title: t("sessions.emptyGuide.step1Title"),
      desc: t("sessions.emptyGuide.step1Desc"),
      action: t("sessions.emptyGuide.step1Action"),
      onClick: () => navigate("/channels?tab=web"),
      testid: "kefu-guide-channels",
    },
    {
      icon: MessageCircle,
      title: t("sessions.emptyGuide.step2Title"),
      desc: t("sessions.emptyGuide.step2Desc"),
      action: t("sessions.emptyGuide.step2Action"),
      onClick: onRefresh,
      testid: "kefu-guide-refresh",
    },
    {
      icon: BookOpen,
      title: t("sessions.emptyGuide.step3Title"),
      desc: t("sessions.emptyGuide.step3Desc"),
      action: t("sessions.emptyGuide.step3Action"),
      onClick: () => navigate("/knowledge"),
      testid: "kefu-guide-knowledge",
    },
  ];
  return (
    <div className="rounded-lg border border-dashed bg-card/40 p-5" data-testid="kefu-empty-guide">
      <p className="text-sm font-semibold text-foreground">{t("sessions.emptyGuide.title")}</p>
      <p className="mt-1 text-xs text-muted-foreground">{t("sessions.emptyGuide.desc")}</p>
      <div className="mt-4 grid gap-3 md:grid-cols-3">
        {steps.map((s) => (
          <div key={s.testid} className="flex flex-col gap-2 rounded-md border bg-background p-3">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              <s.icon className="h-4 w-4 text-primary" />
              {s.title}
            </div>
            <p className="flex-1 text-xs leading-relaxed text-muted-foreground">{s.desc}</p>
            <Button
              size="sm"
              variant="outline"
              className="self-start"
              onClick={s.onClick}
              data-testid={s.testid}
            >
              {s.testid === "kefu-guide-refresh" && <RefreshCw className="mr-1 h-3.5 w-3.5" />}
              {s.action}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
